import type { FeatureGeometryBinding, QuantityUnit } from "./feature";

/**
 * WHAT A RATE IS PER (spec §90-16/17).
 *
 * A rate's meaning used to be carried by a display string: §77 derived
 * every rate that stated a quantity as driver 'each' and threw the unit
 * away, so $58/sq ft of countertop could be multiplied by 24 linear feet of
 * cabinet. The meaning is now structural — a UNIT, plus the SURFACE it
 * measures where square feet alone would be ambiguous (wall sq ft is not
 * floor sq ft, and a roof square is neither).
 *
 * Pure: no I/O. Legacy rates carry no unit at all and keep their pre-§90
 * behaviour; nothing here reinterprets one.
 */
export type RateUnit =
  /** A thing: a toilet, a door, a light. */
  | "each"
  /** Area, in square feet — see RateSurface for which surface. */
  | "sqft"
  /** Length, in linear feet: cabinet runs, baseboard, gutters. */
  | "lf"
  /** A roofing square: 100 square feet of roof (§60J). */
  | "square"
  /** One installed system: an HVAC changeout, a panel, a repipe. */
  | "system"
  /** A proxy over the whole house's living area (§77 — never a measured surface). */
  | "house-sqft"
  /** One figure for the work, with no quantity behind it. */
  | "allowance";

export type RateSurface = "floor" | "wall" | "ceiling" | "roof";

export const RATE_UNITS: ReadonlyArray<{ id: RateUnit; displayName: string; surface?: RateSurface }> = [
  { id: "sqft", displayName: "per floor sq ft", surface: "floor" },
  { id: "sqft", displayName: "per wall sq ft", surface: "wall" },
  { id: "sqft", displayName: "per ceiling sq ft", surface: "ceiling" },
  { id: "sqft", displayName: "per sq ft" },
  { id: "lf", displayName: "per linear ft" },
  { id: "square", displayName: "per roofing square" },
  { id: "each", displayName: "each" },
  { id: "system", displayName: "per system" },
  { id: "house-sqft", displayName: "per house sq ft (proxy)" },
  { id: "allowance", displayName: "one allowance" },
];

/** "per wall sq ft" / "per linear ft" — how a rate reads beside its money. */
export function rateUnitWords(unit: RateUnit | null, surface: RateSurface | null = null): string {
  if (unit === null) return "";
  if (unit === "sqft") {
    return surface === "wall" ? "per wall sq ft" : surface === "ceiling" ? "per ceiling sq ft" : surface === "floor" ? "per floor sq ft" : "per sq ft";
  }
  return RATE_UNITS.find((r) => r.id === unit && r.surface === undefined)?.displayName ?? unit;
}

/** A rate with no quantity behind it prices the work in one figure. */
export const isDimensionless = (unit: RateUnit | null): boolean => unit === "allowance" || unit === "house-sqft";

/**
 * The surface a scope quantity measures, from the binding that produced it.
 * Null when the quantity is not an area (or when nothing says which surface),
 * and an area with no stated surface never matches a rate that names one.
 */
export function surfaceOfBinding(binding: FeatureGeometryBinding | null): RateSurface | null {
  switch (binding) {
    case "room-floor-area":
    case "project-finish-area":
      // A project finish covers floors, walls or ceilings; its own feature's
      // finish_key says which, so the caller passes that surface explicitly.
      return binding === "room-floor-area" ? "floor" : null;
    case "room-wall-area":
      return "wall";
    case "room-ceiling-area":
      return "ceiling";
    case "objects-area":
      return null; // a countertop's area is not a room surface
    default:
      return null;
  }
}

export interface QuantityShape {
  unit: QuantityUnit | null;
  /** Which surface an area measures, when anything knows. */
  surface: RateSurface | null;
}

/**
 * DOES THIS RATE PRICE THIS QUANTITY? (spec §90-20)
 *
 * Units must be the same — nothing converts behind the user's back (the
 * §9E rule) — and when either side names a surface, both must agree.
 * A rate that names no surface prices any area of its unit; a quantity
 * whose surface is unknown never claims a surface-specific rate.
 */
export function rateMatchesQuantity(
  rate: { unit: RateUnit | null; surface: RateSurface | null },
  quantity: QuantityShape,
): boolean {
  if (rate.unit === null) return false; // a legacy rate matches through the old driver path only
  if (isDimensionless(rate.unit)) return true; // priced without a quantity at all
  // One installed system is counted as one thing: a 'system' rate prices an 'each' quantity.
  if (rate.unit === "system") return quantity.unit === "each";
  if (rate.unit !== quantity.unit) return false;
  if (rate.surface === null) return true;
  return rate.surface === quantity.surface;
}

/** How many roof square feet a count of roofing squares covers (explicit only). */
export const ROOF_SQFT_PER_SQUARE = 100;
export const roofSquaresToSqftX100 = (squaresX100: number): number => squaresX100 * ROOF_SQFT_PER_SQUARE;
export const roofSqftToSquaresX100 = (sqftX100: number): number => Math.round(sqftX100 / ROOF_SQFT_PER_SQUARE);
